import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";

import { RelativeTime } from "~/components/relative-time";
import { cn } from "~/components/ui/cn";
import { RefreshIcon } from "~/components/ui/icons/refresh";
import { ShopifyPluggedIcon } from "~/components/ui/icons/shopify-plugged";
import { toast } from "~/components/ui/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useProductsQuery } from "~/graphql/graphql-new.gen";
import {
  useBrandQuery,
  useResyncShopifyStockMutation,
} from "~/graphql/graphql.gen";
import { track } from "~/libs/analytics";
import type { Brand } from "~/stores/brand";
import { activeSaleState } from "~/stores/sales";
import { formatDate } from "~/utils/date";

export function ShopifySyncInfo({
  shopify,
}: {
  shopify: NonNullable<NonNullable<Brand>["shopify"]>;
}) {
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();

  const activeSale = useAtomValue(activeSaleState);

  const { mutateAsync: resyncShopifyStock, isPending } =
    useResyncShopifyStockMutation();

  const handleResyncShopifyStock = async () => {
    if (!activeSale?.id) return;

    try {
      await resyncShopifyStock({ saleId: activeSale.id });

      queryClient.invalidateQueries({
        queryKey: useBrandQuery.getKey(undefined),
      });

      queryClient.invalidateQueries({
        queryKey: useProductsQuery.getKey({ saleId: activeSale.id }),
      });
    } catch {
      toast.error(t("stock.shopify.sync_error"));
    }

    track("ResyncShopifyStock", {
      saleId: activeSale.id,
    });
  };

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1 self-center rounded bg-gray-50 px-2 py-1 font-semibold text-gray-700">
        <ShopifyPluggedIcon title="Shopify" className="size-[1.125rem]" />
        <span className="hidden text-sm sm:inline">
          {t("stock.shopify.synced_with_shopify")}
        </span>
      </div>
      <div className="flex items-center gap-1 text-sm text-gray-500">
        {shopify.lastStockSyncAt ? (
          <Tooltip>
            <TooltipTrigger>
              <time dateTime={shopify.lastStockSyncAt} className="lowercase">
                <RelativeTime dateTime={new Date(shopify.lastStockSyncAt)} />
              </time>
            </TooltipTrigger>
            <TooltipContent>
              {formatDate(
                new Date(shopify.lastStockSyncAt),
                i18n.language.replace("_", "-"),
                {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                },
              )}
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="lowercase">{t("date.never")}</span>
        )}
        <button
          type="button"
          disabled={isPending}
          onClick={handleResyncShopifyStock}
          className="flex items-center gap-x-1 rounded p-1 text-sm font-semibold text-green-900 outline-none hover:bg-green-300 focus-visible:ring-2 focus-visible:ring-[#ECECEC] disabled:opacity-50"
        >
          <RefreshIcon
            className={cn("size-3.5", {
              "animate-spin": isPending,
            })}
          />
        </button>
      </div>
    </div>
  );
}
